import React from "react";

import { useQuery } from "@apollo/client";
import { Box, Grid, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { withStyles } from "tss-react/mui";

import KcalIcon from "@/components/Icons/KcalIcon";
import WeighingScaleIcon from "@/components/Icons/WeighingScaleIcon";
import { graphql } from "@/gql";
import { percent } from "@/utils/number";

const BoldText = withStyles(Typography, {
  root: {
    fontWeight: "bold",
  },
});

const StyledKcalIcon = withStyles(KcalIcon, {
  root: {
    width: "18px",
    height: "18px",
  },
});

const StyledWeightIcon = withStyles(WeighingScaleIcon, {
  root: {
    width: "35px",
    height: "35px",
  },
});

const SystemRecommendedSections = [
  {
    hideWeight: true,
    label: "Maintain Weight",
    percentage: 1,
    kgPerWeek: "0",
    gridSize: 2,
  },
  {
    hideWeight: false,
    label: "Mild weight loss",
    percentage: 0.9,
    kgPerWeek: "0.5",
    gridSize: true, // boolean means auto-sized
  },
  {
    hideWeight: false,
    label: "Weight loss",
    percentage: 0.75,
    kgPerWeek: "1",
    gridSize: true, // boolean means auto-sized
  },
  {
    hideWeight: false,
    label: "Weight gain",
    percentage: 1.5,
    kgPerWeek: "0.5 - 1",
    gridSize: true, // boolean means auto-sized
  },
] as const;

const systemRecommendedCaloriesQuery = graphql(`
  query SystemRecommendedCalories($clientId: ID!) {
    client(id: $clientId) {
      id
      targetDailyEnergyExpenditure
    }
  }
`);

const SystemRecommendedCalories: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { data } = useQuery(systemRecommendedCaloriesQuery, {
    variables: { clientId: clientId ?? "" },
  });

  function caloriesPerDay(fraction: number) {
    return Math.round(
      (data?.client?.targetDailyEnergyExpenditure ?? 0) * fraction
    );
  }

  return (
    <Box>
      <Box marginBottom={3}>
        <BoldText variant="body1">System Recommended</BoldText>
      </Box>
      <Grid container spacing={3}>
        {SystemRecommendedSections.map((s) => (
          <Grid
            key={s.label}
            item
            xs={12}
            sm={6}
            md={s.gridSize}
            container
            direction="column"
          >
            <Box marginBottom={2}>
              <Typography variant="subtitle2">{s.label}</Typography>
            </Box>
            <Box
              display="flex"
              flex={1}
              padding={2}
              bgcolor="#f5f7fa"
              justifyContent="space-between"
              alignItems="center"
            >
              {!s.hideWeight && (
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  paddingRight={1}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    marginRight={2}
                    width="35px"
                    height="35px"
                    borderRadius="100%"
                    bgcolor="#fff"
                  >
                    <StyledWeightIcon />
                  </Box>
                  <Box>
                    <BoldText variant="body1">{s.kgPerWeek}</BoldText>
                    <Typography variant="caption" color="textSecondary">
                      kg / week
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box flex={1} display="flex" alignItems="center" paddingLeft={1}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  marginRight={2}
                  width="35px"
                  height="35px"
                  borderRadius="100%"
                  bgcolor="#fff"
                >
                  <StyledKcalIcon />
                </Box>
                <Box>
                  <BoldText>
                    {caloriesPerDay(s.percentage)}{" "}
                    <Typography
                      component="span"
                      variant="subtitle2"
                      color="inherit"
                    >
                      {percent(s.percentage)}%
                    </Typography>
                  </BoldText>
                  <Typography variant="caption" color="textSecondary">
                    kcal / day
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SystemRecommendedCalories;
